import React, { useState, useEffect, useMemo, useRef } from 'react';
import { CreditCard, Plus, User, Download, X, Clock } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import api from '../components/api';
import { toast } from 'react-toastify';
import useGlobale from '../components/global';
import Sidebar from '../components/SideBar';
import Header from '../components/Header';
import DemandeurMenu from '../components/DemandeurMenu';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import countryList from 'react-select-country-list';
import Select from 'react-select';



const CarteAnpePage = () => {
    const { user } = useGlobale();
    const [loading, setLoading] = useState(true);
    const [carte, setCarte] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    // Liste des pays memoized
    const countries = useMemo(() => countryList().getData(), []);
    const nigerOption = countries.find(country => country.value === 'NE') || { value: 'NE', label: 'Niger' };

    // Initialiser le formulaire avec Niger comme pays par défaut
    const [formData, setFormData] = useState({
        date_delivrance: new Date().toISOString().split('T')[0],
        date_expiration: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        emplois_souhaite: '',
        nationnalite: nigerOption.label
    });

    const carteRef = useRef(null);

    useEffect(() => {
        fetchCarte();
    }, []);

    const fetchCarte = async () => {
        try {
            const response = await api.get('demandeur/carte-anpe/');
            if (response.data) {
                setCarte(response.data);
            }
            setLoading(false);
        } catch (error) {
            console.error('Erreur:', error);
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleCountryChange = (selectedOption) => {
        setFormData(prev => ({
            ...prev,
            nationnalite: selectedOption.label
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            // Ajouter l'ID de l'utilisateur
            const dataToSend = {
                ...formData,
                utilisateur: user.id
            };
            
            const response = await api.post('demandeur/carte-anpe/', dataToSend);
            setCarte(response.data);
            setShowModal(false);
            toast.success('Carte ANPE créée avec succès');
        } catch (error) {
            console.error('Erreur lors de la création:', error.response?.data);
            toast.error(error.response?.data?.detail || 'Erreur lors de la création de la carte');
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const renderCarteDetails = () => {
        if (!carte) return null;

        return (
            <div className="flex flex-col space-y-6">
                <div ref={carteRef} className="max-w-4xl mx-auto">
                    {/* Carte avec bordure dorée */}
                    <div className="bg-white border-2 border-[#FFD700]">
                        {/* En-tête vert */}
                        <div className="bg-[#437011] p-4">
                            <div className="flex items-start space-x-4">
                                {/* Logo ANPE */}
                                <img 
                                    src="/logo-anpe.png" 
                                    alt="Logo ANPE" 
                                    className="h-24 w-24 object-contain bg-white p-1"
                                />
                                {/* Informations de l'en-tête */}
                                <div className="flex-1 text-center text-white">
                                    <h2 className="text-lg font-bold mb-1">République du Niger</h2>
                                    <p className="text-base font-semibold mb-1">AGENCE NATIONALE POUR LA PROMOTION DE L'EMPLOI (ANPE)</p>
                                    <p className="text-sm mb-2">ETABLISSEMENT PUBLIC À CARACTÈRE ADMINISTRATIF</p>
                                    <div className="text-xs space-y-1">
                                        <p>BP: 13222 Niamey - Tel : +227 20 73 33 84 - Tél/Fax: +227 20 73 70 31</p>
                                        <p>Email: anpe.niger16@gmail.com - http//www.anpe-niger.ne</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Contenu principal */}
                        <div className="p-6">
                            {/* Numéro de carte */}
                            <div className="text-sm mb-4">
                                <p>N°{carte.numero}/ANPE</p>
                            </div>

                            {/* Titre */}
                            <h3 className="text-xl font-bold text-center mb-6">CARTE D'INSCRIPTION</h3>

                            <div className="flex gap-6">
                                {/* Photo */}
                                <div className="w-40 h-48 border-2 border-gray-300">
                                    {user?.photo_url ? (
                                        <img 
                                            src={user.photo_url} 
                                            alt="Photo" 
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                                            <User size={64} className="text-gray-400" />
                                        </div>
                                    )}
                                </div>

                                {/* Informations personnelles */}
                                <div className="flex-1 space-y-3">
                                    <div className="grid gap-2">
                                        <p><span className="font-semibold">Nom :</span> {user?.last_name}</p>
                                        <p><span className="font-semibold">Prénoms :</span> {user?.first_name}</p>
                                        <p><span className="font-semibold">Sexe :</span> {user?.sexe === 'M' ? 'Masculin' : 'Masculin'}</p>
                                        <p><span className="font-semibold">Nationalité :</span> {carte.nationnalite}</p>
                                        <p><span className="font-semibold">Emploi(s) Souhaité(s) :</span> {carte.emplois_souhaite}</p>
                                        <p><span className="font-semibold">Date D'inscription :</span> {formatDate(carte.date_delivrance)}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Pied de carte */}
                        <div className="p-4">
                            <div className="text-center space-y-2">
                                <p className="font-bold">DATE DE VALIDITÉ DE LA CARTE</p>
                                <p>Cette carte est valide pour toute opération à l'ANPE, jusqu'à la date du {formatDate(carte.date_expiration)}</p>
                            </div>
                            <div className="mt-4 flex justify-center">
                                <QRCodeSVG 
                                    value={carte.numero}
                                    size={100}
                                    level="H"
                                    includeMargin={true}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Bouton de téléchargement */}
                <div className="flex justify-center mt-4">
                    <button
                        onClick={handleDownloadPDF}
                        className="bg-[#437011] text-white px-6 py-3 rounded-lg hover:bg-[#365D0E] transition-colors duration-200 flex items-center space-x-2"
                    >
                        <Download size={20} />
                        <span>Télécharger la carte</span>
                    </button>
                </div>
            </div>
        );
    };

    const handleDownloadPDF = async () => {
        if (!carteRef.current) return;

        try {
            // Afficher un message de chargement
            toast.info('Préparation du téléchargement...', { autoClose: 2000 });
            
            const canvas = await html2canvas(carteRef.current, {
                scale: 2,
                useCORS: true,
                logging: false,
                backgroundColor: '#ffffff'
            });

            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4'
            });

            // Calculer les dimensions pour centrer l'image sur la page
            const imgWidth = 210; // Largeur A4 en mm
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            const yPosition = (297 - imgHeight) / 2; // 297mm est la hauteur A4

            pdf.addImage(imgData, 'PNG', 0, yPosition, imgWidth, imgHeight);
            pdf.save(`Carte_ANPE_${carte.numero}.pdf`);
            
            toast.success('Carte téléchargée avec succès!');
        } catch (error) {
            console.error('Erreur lors de la génération du PDF:', error);
            toast.error('Erreur lors de la génération du PDF');
        }
    };

    return (
        <div className="flex min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
            <Sidebar
                isOpen={isSidebarOpen}
                menuItems={DemandeurMenu()}
                onClose={() => setIsSidebarOpen(false)}
            />

            <div className="flex-1">
                <Header
                    isSidebarOpen={isSidebarOpen}
                    setIsSidebarOpen={setIsSidebarOpen}
                    user={user}
                />
                
                <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center h-64">
                            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-green-600"></div>
                            <p className="mt-4 text-gray-600 font-medium">Chargement en cours...</p>
                        </div>
                    ) : carte ? (
                        renderCarteDetails()
                    ) : (
                        <div className="bg-white rounded-2xl shadow-xl p-10 max-w-3xl mx-auto border border-gray-50">
                            <div className="mb-8 text-center">
                                <div className="h-24 w-24 rounded-full bg-gradient-to-br from-orange-100 to-green-100 flex items-center justify-center text-orange-500 mx-auto mb-4 border-4 border-white shadow-md">
                                    <CreditCard size={40} strokeWidth={1.5} />
                                </div>
                                <h3 className="text-2xl font-bold text-gray-800 mb-3">
                                    Vous n'avez pas encore de carte ANPE
                                </h3>
                                <div className="h-1 w-24 bg-gradient-to-r from-green-500 to-orange-500 mx-auto rounded-full"></div>
                            </div>
                            
                            <p className="text-gray-600 mb-8 max-w-md mx-auto text-center leading-relaxed">
                                La carte ANPE est un document officiel qui vous identifie comme demandeur d'emploi. 
                                Elle vous permettra d'accéder aux services et opportunités proposés par l'ANPE.
                            </p>
                            
                            <div className="bg-gray-50 p-6 rounded-xl mb-8">
                                <h4 className="font-medium text-gray-800 mb-2 flex items-center">
                                    <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center text-green-600 mr-2">
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                                            <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    Avantages de la carte ANPE
                                </h4>
                                <ul className="space-y-2 text-sm text-gray-600 ml-8">
                                    <li className="flex items-start">
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-green-500 mr-2 mt-0.5">
                                            <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                                        </svg>
                                        Accès prioritaire aux offres d'emploi
                                    </li>
                                    <li className="flex items-start">
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-green-500 mr-2 mt-0.5">
                                            <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                                        </svg>
                                        Participation aux formations professionnelles
                                    </li>
                                    <li className="flex items-start">
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-green-500 mr-2 mt-0.5">
                                            <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                                        </svg>
                                        Reconnaissance officielle de votre statut
                                    </li>
                                </ul>
                            </div>
                            
                            <div className="text-center">
                                <button
                                    onClick={() => setShowModal(true)}
                                    className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl hover:shadow-lg transition-all duration-300 group"
                                >
                                    <Plus className="mr-2 group-hover:rotate-90 transition-transform duration-300" size={20} />
                                    <span className="font-semibold">Créer ma carte ANPE</span>
                                </button>
                            </div>
                        </div>
                    )}
                </main>
            </div>

            {/* Modal de création de carte amélioré */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md border border-gray-100 overflow-hidden">
                        <div className="bg-gradient-to-r from-green-600 to-orange-500 p-6 text-white">
                            <div className="flex justify-between items-center">
                                <h3 className="text-xl font-bold">
                                    Créer une carte ANPE
                                </h3>
                                <button
                                    onClick={() => setShowModal(false)}
                                    className="text-white bg-white bg-opacity-20 rounded-full p-1 hover:bg-opacity-30 transition-colors"
                                >
                                    <X size={20} />
                                </button>
                            </div>
                            <p className="text-white text-opacity-80 text-sm mt-2">
                                Complétez les informations pour obtenir votre carte de demandeur d'emploi
                            </p>
                        </div>
                        
                        <form onSubmit={handleSubmit} className="p-6 space-y-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Nationalité <span className="text-red-500">*</span>
                                </label>
                                <Select
                                    options={countries}
                                    value={countries.find(country => country.label === formData.nationnalite) || nigerOption}
                                    onChange={handleCountryChange}
                                    className="w-full"
                                    classNamePrefix="select"
                                    placeholder="Sélectionnez votre nationalité"
                                    defaultValue={nigerOption}
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            borderColor: '#d1d5db',
                                            boxShadow: 'none',
                                            '&:hover': {
                                                borderColor: '#9ca3af',
                                            },
                                            borderRadius: '0.5rem',
                                            padding: '2px'
                                        }),
                                        option: (base, state) => ({
                                            ...base,
                                            backgroundColor: state.isSelected 
                                                ? '#f97316' 
                                                : state.isFocused 
                                                ? '#fed7aa' 
                                                : base.backgroundColor,
                                            color: state.isSelected ? 'white' : '#374151',
                                        })
                                    }}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Emplois souhaités <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    name="emplois_souhaite"
                                    value={formData.emplois_souhaite}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                                    rows="3"
                                    placeholder="Ex: Développeur web, Assistant administratif..."
                                    required
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Date de délivrance
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="date"
                                            value={formData.date_delivrance}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
                                            disabled
                                        />
                                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                            <Clock size={18} className="text-gray-400" />
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Date d'expiration
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="date"
                                            value={formData.date_expiration}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
                                            disabled
                                        />
                                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                            <Clock size={18} className="text-gray-400" />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="flex justify-end space-x-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="px-5 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    className="px-5 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:shadow-md transition-all duration-300 font-medium"
                                >
                                    Créer ma carte
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Message de confirmation de création */}
          
        </div>
    );
};

export default CarteAnpePage;


// Ajout de la fonction utilitaire pour formater le numéro de carte
export const formatCardNumber = (cardNumber) => {
    if (!cardNumber) return '';
    
    // Format: ANPE-YYYY-XXXXXX
    const parts = cardNumber.split('-');
    if (parts.length !== 3) return cardNumber;
    
    return `${parts[0]}-${parts[1]}-${parts[2]}`;
};

